import { useMutation } from "@tanstack/react-query";
import { Dropdown } from "primereact/dropdown";
import { useState, useEffect } from "react";
import { DCSQueryClient } from "../../../../../queryClient";
import { If } from "../../../../../components/If/If";
import { useToast } from "../../../../../Providers/ToastProvider";
import { requests } from "../../../../../services/http-common";
import { LeadStatusEnum } from "../../Lead.model";
import { DenyReasonPopup } from "./DenyReasonPopup/DenyReasonPopup";

import classes from "./Status.module.scss";
import { useUser } from "../../../../../keycloak/hooks";
import { UserRoles } from "../../../../../constants/userRoles";

const selectedStatusLabels: { [key in LeadStatusEnum]: string } = {
  [LeadStatusEnum.Pending]: "Pending",
  [LeadStatusEnum.Approved]: "Approved",
  [LeadStatusEnum.Denied]: "Denied",
  [LeadStatusEnum.Inprogress]: "In Progress",
};

const statusOptions = [
  { label: "Pending", value: LeadStatusEnum.Pending, disabled: true },
  { label: "Approve", value: LeadStatusEnum.Approved },
  { label: "Deny", value: LeadStatusEnum.Denied },
];

export const Status = (rowData: {
  status: LeadStatusEnum;
  id: string;
  sendStatus: boolean;
}) => {
  const [leadStatus, setStatus] = useState<LeadStatusEnum>(rowData.status);
  const [openDenyReasonPopup, setOpenDenyReasonPopup] = useState(false);

  const { show } = useToast();
  const updateLeadStatus = useMutation({
    mutationFn: (newLead: any) => {
      return requests.put(`/lead/status/${rowData.id}`, { ...newLead });
    },
    onSuccess: () => {
      show({ detail: "Status updated !" });
      DCSQueryClient.invalidateQueries({ queryKey: ["leads-list"] });
      setOpenDenyReasonPopup(false);
    },
    onError: (err: any) => {
      show({
        severity: "error",
        detail: err.response.data.error.message || "Something went wrong",
      });
      setStatus(rowData.status);
    },
  });

  const onStatusChange = (e: any) => {
    setStatus(e.target.value);
    if (e.target.value === LeadStatusEnum.Denied) {
      setOpenDenyReasonPopup(true);
      return;
    }
    updateLeadStatus.mutate({ status: e.target.value });
  };

  const valueTemplate = (option: { label: string; value: LeadStatusEnum }) => {
    return selectedStatusLabels[option?.value];
  };

  useEffect(() => {
    if (rowData.status) {
      setStatus(rowData.status);
    }
  }, [rowData.status]);

  const hideDenyReasonPopup = () => {
    setStatus(rowData.status);
    setOpenDenyReasonPopup(false);
  };

  const user: any = useUser();

  return (
    <>
      <Dropdown
        appendTo="self"
        options={statusOptions}
        onChange={onStatusChange}
        className={classes[selectedStatusLabels[leadStatus]]}
        value={leadStatus}
        style={{ minWidth: "140px" }}
        id="status"
        disabled={
          leadStatus !== LeadStatusEnum.Pending ||
          rowData.sendStatus ||
          !user?.roles.dcs_client.includes(UserRoles.LEAD_UPDATE)
        }
        valueTemplate={valueTemplate}
      />
      <If condition={openDenyReasonPopup}>
        <DenyReasonPopup
          onHide={hideDenyReasonPopup}
          updateLeadStatus={updateLeadStatus}
        />
      </If>
    </>
  );
};
